/**
 * useTabAccent — web sibling of iOS Aperture per-tab identity.
 *
 * Reads the current route pathname and sets
 * `document.documentElement.dataset.orbitTab = <tab>` — which the
 * CSS variable stack in `src/index.css` picks up via
 * `html[data-orbit-tab="..."]` blocks to shift the ambient body
 * backdrop's glow colours per destination.
 *
 * Mirrors the ten Aperture identities from iOS, plus POL-19's
 * `emails` sibling that peels /emails off of /settings so its
 * ambient reads distinct:
 *   chat · today · passport · projects · clients · calendar ·
 *   expenses · galleries · booking · settings · emails
 *
 * Unmapped routes fall through to the default (orbit orange /
 * lunar teal) so surprise routes still render on-brand.  The
 * only side effect is on <html> so any child (page, modal,
 * popover) inherits the correct accent through CSS inheritance.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSimplifiedNav } from '@/hooks/useSimplifiedNav';
// BRAND-2 — the one route → accent registry.  This hook only
// TRANSPORTS the mapping onto <html>; the map itself lives with the
// nav destinations so chrome and pages can't drift.
import { sectionAccentForNavId } from '@/lib/navDestinations';

/// Map a route path to its Aperture identity.  Longest-prefix
/// match so `/settings/templates` still resolves to `settings`.
function tabForPath(pathname: string): string | undefined {
  // Order matters — longest / most specific prefixes first.
  const map: Array<[string, string]> = [
    // ── Mobile shell (/m/*) — preview + Capacitor build ──
    ['/m/today', 'today'],
    ['/m/clients', 'clients'],
    ['/m/messages', 'chat'],
    ['/m/settings', 'settings'],
    ['/m', 'today'],   // MobileShell root defaults to today
    // ── Desktop web ──
    // Nova Design is now a mode inside Nova (/assistant?mode=design),
    // so the old /nova-design entry is gone — the route just
    // redirects and never renders.
    ['/assistant', 'chat'],
    ['/galaxy', 'passport'],
    ['/projects', 'projects'],
    ['/studio/', 'projects'],
    ['/clients', 'clients'],
    ['/inquiries', 'clients'],
    ['/leads', 'clients'],
    ['/calendar', 'calendar'],
    ['/expenses', 'expenses'],
    ['/galleries', 'galleries'],
    ['/deliveries', 'galleries'],
    // BRAND-2 — Sites owns its own identity (Instrument Teal).  It
    // used to fold into 'galleries', but galleries is deliberately
    // near-neutral silver BECAUSE it's a wall of photographs — the
    // site builder is chrome+forms and deserves an actual colour.
    ['/sites', 'sites'],
    // POL-19 — Emails now owns its own identity ('emails') so the
    // ambient backdrop reads mint on /emails vs teal on /settings,
    // even though the two shells share the same section-rail
    // vocabulary.  Was: 'settings' (teal) — read as a Settings
    // sub-tab and made the Emails Center feel un-differentiated.
    ['/emails', 'emails'],
    ['/settings', 'settings'],
    ['/billing', 'settings'],
    ['/book', 'booking'],
    ['/', 'today'],   // catch-all last — Index / dashboard
  ];
  for (const [prefix, tab] of map) {
    if (pathname === prefix || pathname.startsWith(prefix + '/') || pathname === prefix) {
      return tab;
    }
    if (prefix === '/' && pathname === '/') return tab;
  }
  return undefined;
}

// NAV-6 wave (a) — when the simplified six-tab nav is on, sub-routes
// inherit their PARENT destination's accent so the whole group reads
// as one identity:
//   Work     (/projects) owns Calendar + Booking
//   Business (/galleries) owns Sites + Emails
// /inquiries → clients and /sites → galleries already fold in via the
// classic map above, so only these two identities need re-filing.
// BRAND-2 — the emails → galleries fold is GONE: Business no longer
// reads as one identity because galleries is deliberately quiet
// silver (photos win) while sites/emails wear Instrument Teal.
// Folding Emails into silver made the Emails Center read as
// furniture.  Calendar still folds into Work's violet.
const SIMPLIFIED_PARENT_OVERRIDES: Record<string, string> = {
  calendar: 'projects',   // Work
};

export function useTabAccent(): void {
  const { pathname, search } = useLocation();
  const [simplifiedNav] = useSimplifiedNav();

  useEffect(() => {
    const rawTab = tabForPath(pathname);
    // Booking lives at /settings?tab=scheduling — path-only mapping
    // can't see the query, so special-case it here.  Booking wears
    // Work's violet in BOTH nav modes (BRAND-2).
    const isBookingTab =
      pathname.startsWith('/settings') && search.includes('tab=scheduling');
    let tab = rawTab;
    if (simplifiedNav) {
      if (isBookingTab) {
        tab = 'projects';
      } else if (tab && SIMPLIFIED_PARENT_OVERRIDES[tab]) {
        tab = SIMPLIFIED_PARENT_OVERRIDES[tab];
      }
    }
    const html = document.documentElement;
    if (tab) {
      html.dataset.orbitTab = tab;
    } else {
      delete html.dataset.orbitTab;
    }
    // BRAND-2 — section accent.  Same route resolution, but mapped
    // through the one accent registry and written as its OWN
    // attribute: `data-orbit-tab` drives the ambient glow identity
    // (dark-tone, theme-independent), while `data-orbit-accent`
    // remaps the theme-aware `--section-accent` pair that page
    // content (eyebrows, feature CTAs, focus rings, empty states)
    // consumes.  Unmapped routes drop the attribute → Mission Blue.
    const accentId = isBookingTab ? 'booking' : rawTab;
    if (accentId) {
      html.dataset.orbitAccent = sectionAccentForNavId(accentId);
    } else {
      delete html.dataset.orbitAccent;
    }
    // Cleanup only when the component unmounts — we deliberately
    // don't clear on every path change because the next effect
    // will overwrite it.  A stray unmount (RootLayout out of tree)
    // clears the attribute so unauthenticated pages default back
    // to orbit orange.
    return () => {
      // No-op — leaving the last-set tab in place through route
      // transitions means the background image doesn't flash
      // during navigation.
    };
  }, [pathname, search, simplifiedNav]);
}
